import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Smartphone, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { IconBadge } from '@/components/ui/card'
import { useInstallStore, shouldShowInstallPrompt } from '@/stores/install.store'
import { IosInstallDialog } from './IosInstallDialog'

/* Spec PWA-001 FR-031 / US-004 — aviso flutuante de instalacao, nas telas com a barra de navegacao.
   Aparece uma vez por sessao do navegador, alguns segundos depois de entrar, so quando o navegador permite
   instalar (Android/Chrome/Edge: convite nativo · iOS: instrucao). "Agora nao" respeita os 30 dias do FR-031;
   o X so fecha nesta sessao. Nao e modal: nao prende o foco nem bloqueia a tela. */

const SESSION_KEY = 'gpd-install-prompt-shown'
const DELAY_MS = 2500

function alreadyShownThisSession(): boolean {
    try { return sessionStorage.getItem(SESSION_KEY) === '1' } catch { return false }
}

function markShownThisSession() {
    try { sessionStorage.setItem(SESSION_KEY, '1') } catch { /* storage bloqueado */ }
}

export function InstallPrompt() {
    const { t } = useTranslation()
    const { availability, dismissedAt, dismiss, install, setFloatingVisible } = useInstallStore()
    const [open, setOpen] = useState(false)
    const [iosOpen, setIosOpen] = useState(false)
    const eligible = shouldShowInstallPrompt(availability, dismissedAt, Date.now())

    useEffect(() => {
        if (!eligible || alreadyShownThisSession()) return
        const id = setTimeout(() => {
            markShownThisSession()
            setOpen(true)
        }, DELAY_MS)
        return () => clearTimeout(id)
    }, [eligible])

    const visible = open && eligible
    useEffect(() => {
        setFloatingVisible(visible)
        return () => setFloatingVisible(false)
    }, [visible, setFloatingVisible])

    if (!visible && !iosOpen) return null

    const onInstall = async () => {
        if (availability.kind === 'ios-manual') {
            setIosOpen(true)
            setOpen(false)
            return
        }
        await install()
        setOpen(false)
    }

    return (
        <>
            {visible ? (
                <section
                    aria-labelledby="install-prompt-title"
                    aria-describedby="install-prompt-body"
                    className="fixed inset-x-3 bottom-[calc(5.5rem+var(--spacing-safe-bottom))] z-30 mx-auto flex max-w-lg flex-col gap-3 rounded-[20px] border border-line bg-surface p-4 shadow-[0_18px_40px_-18px_rgb(0_0_0/0.45)] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 lg:inset-x-auto lg:right-6 lg:bottom-6 lg:w-[24rem]"
                >
                    <div className="flex items-start gap-3">
                        <IconBadge><Smartphone className="size-5" aria-hidden="true" /></IconBadge>
                        <div className="flex min-w-0 grow flex-col gap-0.5">
                            <h2 id="install-prompt-title" className="m-0 text-[0.95rem] font-bold">{t('sponsorInstall.promptTitle')}</h2>
                            <p id="install-prompt-body" className="m-0 text-[0.82rem] leading-snug text-ink-muted">{t('sponsorInstall.promptBody')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label={t('sponsorCommon.close')}
                            className="-mt-1 -mr-1 flex size-11 shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-ink-muted hover:bg-ink/5"
                        >
                            <X className="size-5" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="ghost" size="sm" className="grow" onClick={() => { dismiss(); setOpen(false) }}>
                            {t('sponsorInstall.later')}
                        </Button>
                        <Button variant="primary" size="sm" className="grow" onClick={() => void onInstall()}>
                            {t('sponsorInstall.action')}
                        </Button>
                    </div>
                </section>
            ) : null}
            <IosInstallDialog open={iosOpen} onClose={() => setIosOpen(false)} />
        </>
    )
}

/* Item "Instalar o app" para Minha conta: sempre disponivel quando o navegador permite instalar,
   mesmo depois de o padrinho ter dispensado o convite. */
export function useInstallAction() {
    const { availability, install } = useInstallStore()
    const [iosOpen, setIosOpen] = useState(false)
    const available = availability.kind === 'prompt' || availability.kind === 'ios-manual'
    const run = () => {
        if (availability.kind === 'ios-manual') setIosOpen(true)
        else void install()
    }
    return { available, run, iosOpen, closeIos: () => setIosOpen(false) }
}
